import React, { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Loader2, Phone, Instagram, Facebook } from "lucide-react";
import { Reveal, LineReveal } from "../lib/motion";
import { CATEGORY_OPTIONS, CONTACT } from "../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", phone: "", business: "", category: "", message: "", promo_interest: true, company_website: "" };

export const Contact = () => {
  const [form, setForm] = useState(initial);
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [err, setErr] = useState("");

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.name || !form.email || !form.category) {
      setErr("Please fill in your name, email, and select a service.");
      return;
    }
    setStatus("loading");
    try {
      await axios.post(`${API}/leads`, form);
      setStatus("success");
      setForm(initial);
    } catch (e2) {
      setStatus("error");
      setErr("Something went wrong. Please call us at " + CONTACT.phone + ".");
    }
  };

  const field =
    "w-full bg-transparent border-b border-gold/40 text-cream placeholder-cream/40 py-3 focus:outline-none focus:border-gold transition-colors duration-300";

  return (
    <section id="contact" className="relative bg-navy-deep grain topo py-24 md:py-32 overflow-hidden">
      <div className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-gold/10 blur-[100px]" />
      <div className="relative z-10 mx-auto max-w-[1200px] px-6 md:px-10 grid lg:grid-cols-2 gap-14 lg:gap-20">
        <div>
          <Reveal>
            <p className="text-gold text-xs uppercase tracking-luxe mb-4">Let's Connect</p>
            <h2 className="font-display uppercase text-cream text-4xl md:text-6xl lg:text-7xl leading-[0.9] tracking-tight">
              <LineReveal lines={["Let's advance", "your story."]} />
            </h2>
            <p className="mt-8 text-cream/70 font-light leading-relaxed max-w-md md:text-lg">
              Tell us who you are and how you'd like to show up. We'll reach out to
              get you started — including our <strong className="text-gold font-medium">$50 for 5 posts</strong> test-run offer.
            </p>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mt-12 space-y-5">
              <a href={CONTACT.phoneHref} data-testid="contact-phone" className="flex items-center gap-4 text-cream hover:text-gold transition-colors duration-300 group">
                <span className="h-11 w-11 rounded-full border border-gold/40 flex items-center justify-center group-hover:bg-gold group-hover:text-navy-deep transition-colors duration-300"><Phone size={18} /></span>
                <span className="tracking-wide">{CONTACT.phone}</span>
              </a>
              <a href={CONTACT.instagram} target="_blank" rel="noreferrer" data-testid="contact-instagram" className="flex items-center gap-4 text-cream hover:text-gold transition-colors duration-300 group">
                <span className="h-11 w-11 rounded-full border border-gold/40 flex items-center justify-center group-hover:bg-gold group-hover:text-navy-deep transition-colors duration-300"><Instagram size={18} /></span>
                <span className="tracking-wide">@kingdomadvancementsmm</span>
              </a>
              <a href={CONTACT.facebook} target="_blank" rel="noreferrer" data-testid="contact-facebook" className="flex items-center gap-4 text-cream hover:text-gold transition-colors duration-300 group">
                <span className="h-11 w-11 rounded-full border border-gold/40 flex items-center justify-center group-hover:bg-gold group-hover:text-navy-deep transition-colors duration-300"><Facebook size={18} /></span>
                <span className="tracking-wide">Kingdom Advancement</span>
              </a>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="border border-gold/25 rounded-2xl p-7 md:p-10 bg-white/[0.03] backdrop-blur-sm">
            <AnimatePresence mode="wait">
              {status === "success" ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-12"
                  data-testid="contact-success"
                >
                  <div className="mx-auto h-16 w-16 rounded-full bg-gold flex items-center justify-center mb-6">
                    <Check size={30} className="text-navy-deep" strokeWidth={3} />
                  </div>
                  <h3 className="font-display text-3xl text-cream mb-3">Thank you.</h3>
                  <p className="text-cream/70 font-light">Your request is in. We'll be in touch shortly to advance your story.</p>
                  <button onClick={() => setStatus("idle")} className="mt-8 text-gold text-xs uppercase tracking-luxe hover:underline">
                    Send another request
                  </button>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  onSubmit={submit}
                  data-testid="contact-form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="space-y-6"
                >
                  <div className="grid sm:grid-cols-2 gap-6">
                    <input data-testid="input-name" className={field} placeholder="Full name *" value={form.name} onChange={update("name")} />
                    <input data-testid="input-email" type="email" className={field} placeholder="Email *" value={form.email} onChange={update("email")} />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-6">
                    <input data-testid="input-phone" className={field} placeholder="Phone" value={form.phone} onChange={update("phone")} />
                    <input data-testid="input-business" className={field} placeholder="Business / Organization" value={form.business} onChange={update("business")} />
                  </div>
                  <select data-testid="input-category" className={`${field} [&>option]:text-navy-deep`} value={form.category} onChange={update("category")}>
                    <option value="">Which best describes you? *</option>
                    {CATEGORY_OPTIONS.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                  <textarea data-testid="input-message" rows={3} className={`${field} resize-none`} placeholder="Tell us about your story…" value={form.message} onChange={update("message")} />

                  {/* honeypot — hidden from humans, bots fill it */}
                  <input
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    className="hidden"
                    value={form.company_website}
                    onChange={update("company_website")}
                  />


                  <label className="flex items-center gap-3 text-cream/70 text-sm cursor-pointer select-none">
                    <input
                      type="checkbox"
                      data-testid="input-promo"
                      checked={form.promo_interest}
                      onChange={(e) => setForm({ ...form, promo_interest: e.target.checked })}
                      className="h-4 w-4 accent-gold"
                    />
                    I'm interested in the $50 for 5 posts test-run offer
                  </label>

                  {err && <p className="text-red-300 text-sm" data-testid="contact-error">{err}</p>}

                  <button
                    type="submit"
                    data-testid="contact-submit"
                    disabled={status === "loading"}
                    className="w-full inline-flex items-center justify-center gap-2 bg-gold text-navy-deep font-semibold uppercase tracking-[0.15em] text-xs px-8 py-4 rounded-full hover:bg-gold-light transition-colors duration-300 disabled:opacity-70"
                  >
                    {status === "loading" ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : "Advance My Story"}
                  </button>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
